import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { news, newsCategories } from "@/mocks/news";
import { getNewsById } from "@/lib/data";
import type { NewsArticle } from "@/types/cinema";

export default function NewsPage() {
  const [searchParams] = useSearchParams();
  const initialId = searchParams.get("id") || "";
  const [category, setCategory] = useState("全部");
  const [selected, setSelected] = useState<NewsArticle | null>(getNewsById(initialId) || null);

  useEffect(() => {
    if (initialId) {
      const found = getNewsById(initialId);
      if (found) setSelected(found);
    }
  }, [initialId]);

  useEffect(() => {
    if (selected) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [selected]);

  const filtered = useMemo(
    () => news.filter((n) => category === "全部" || n.category === category),
    [category],
  );

  const related = useMemo(
    () => news.filter((n) => n.id !== selected?.id).slice(0, 3),
    [selected],
  );

  const chipClass = (active: boolean) =>
    `whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
      active
        ? "bg-primary-500 text-background-50"
        : "border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-400 hover:text-primary-600"
    }`;

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影讯专题" }]} />

        <div className="mt-5 mb-6 max-w-3xl">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            影讯专题
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600">
            汇聚最新电影资讯、行业观察与专业影评，带你第一时间了解银幕内外的动态与故事。
          </p>
        </div>

        <div className="mb-6 flex flex-wrap gap-2">
          {newsCategories.map((c) => (
            <button key={c} type="button" onClick={() => setCategory(c)} className={chipClass(category === c)}>
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {filtered.length === 0 ? (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center text-sm text-foreground-500">
                该分类暂无文章，试试其它分类。
              </div>
            ) : (
              <div className="space-y-5">
                {filtered.map((article) => (
                  <article
                    key={article.id}
                    className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 sm:flex-row md:p-5"
                  >
                    <button
                      type="button"
                      onClick={() => setSelected(article)}
                      className="h-40 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-32 sm:w-48"
                    >
                      <img
                        src={article.cover}
                        alt={`${article.title} 配图`}
                        title={`${article.title} 九七电影院影讯`}
                        className="h-full w-full object-cover object-top"
                      />
                    </button>
                    <div className="flex flex-1 flex-col">
                      <span className="mb-2 inline-flex w-fit items-center rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-medium text-accent-900">
                        {article.category}
                      </span>
                      <button
                        type="button"
                        onClick={() => setSelected(article)}
                        className="text-left font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors hover:text-primary-600 md:text-lg"
                      >
                        {article.title}
                      </button>
                      <p className="mt-2 flex-1 text-sm text-foreground-500 line-clamp-2">
                        {article.summary}
                      </p>
                      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-foreground-400">
                        <span>{article.date}</span>
                        <span>{article.author}</span>
                        <button
                          type="button"
                          onClick={() => setSelected(article)}
                          className="text-primary-600 hover:underline"
                        >
                          阅读全文 →
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-fire-line text-accent-600"></i>相关文章推荐
              </h2>
              <ul className="mt-4 space-y-4">
                {related.map((article) => (
                  <li key={article.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(article)}
                      className="group flex gap-3 text-left"
                    >
                      <span className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                        <img
                          src={article.cover}
                          alt={`${article.title} 配图`}
                          title={`${article.title} 九七电影院影讯`}
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                      <span>
                        <span className="block text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-600 line-clamp-2">
                          {article.title}
                        </span>
                        <span className="mt-1 block text-xs text-foreground-400">{article.date}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-primary-950/70 p-4 py-10">
          <div className="relative w-full max-w-2xl rounded-lg bg-background-50 p-5 md:p-8">
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-600 transition-colors hover:bg-background-200"
              aria-label="关闭"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <span className="inline-flex items-center rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
              {selected.category}
            </span>
            <h2 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
              {selected.title}
            </h2>
            <p className="mt-2 text-xs text-foreground-400">
              {selected.date} · {selected.author}
            </p>
            <div className="mt-4 h-56 w-full overflow-hidden rounded-lg bg-background-200 md:h-72">
              <img
                src={selected.cover}
                alt={`${selected.title} 配图`}
                title={`${selected.title} 九七电影院影讯`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="mt-5 space-y-4 text-sm leading-relaxed text-foreground-700">
              {selected.content.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {selected.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}